import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { HashRouter as Router, Route, useHistory, useLocation } from 'react-router-dom';
import { Logo } from '@lightspeed/cirrus-ls-logo';

import { Page } from '~/renderer/components/Page/Page.style';
import Receipt from '~/renderer/components/Receipt';
import { ReceiptBackground } from '~/renderer/components/Receipt/Receipt.style';
import SocketConnection from '~/renderer/components/SocketConnection';
import StatusIndicator from '~/renderer/components/StatusIndicator';
import WaitForRetail from '~/renderer/components/WaitForRetail';
import { useClearDisplayBeforeUnload } from '~/renderer/hooks/use-clear-display-before-unload';
import useGetVersion from '~/renderer/hooks/use-get-version';
import { useResizeCFD } from '~/renderer/hooks/use-resize-cfd';
import { RegisterCustomer } from '~/renderer/modules/RegisterCustomer';
import { ConfirmSaleButton } from '~/renderer/modules/Tipping/ConfirmSaleButton';
import {
  ROUTE_REGISTER_CUSTOMER,
  ROUTE_TERMINAL_PAYMENT,
  ROUTE_TIPPING_FLOW_INPUT,
} from '~/renderer/routes';
import { getCfdOptions } from '~/renderer/store/cfd/selectors';
import { getIsConnected } from '~/renderer/store/connection/selectors';
import { getIsFullscreen, getSelectedDisplay } from '~/renderer/store/displays/selectors';
import { receiptActions } from '~/renderer/store/receipt';
import {
  DEFAULT_BACKGROUND_IMAGE,
  isBrowserCFD,
  isCustomerCFD,
  isRenderProcess,
} from '~/renderer/utils';

import { TerminalPayment } from '../modules/Tipping/TerminalPayment';
import { TipInput } from '../modules/Tipping/TipInput';
import { getIsCustomerInfoVisible, getIsPaymentStaged, getTip } from '../store/receipt/selectors';
import { Tip } from '../store/receipt/types';

import type { ConnectionState } from '~/renderer/store/connection/types';
import type { AppState } from '~/renderer/store/types';

const CFDRouter = ({ children }: { children: React.ReactChild[] }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const isPaymentStaged = useSelector<AppState, boolean | null>(getIsPaymentStaged);
  const tip = useSelector<AppState, Tip | null>(getTip);
  const showCustomerInfo = useSelector(getIsCustomerInfoVisible);

  React.useEffect(() => {
    // If Payment is Staged we navigated to Terminal Payment Screen
    if (isPaymentStaged && ['/', ROUTE_TIPPING_FLOW_INPUT].includes(pathname)) {
      history.push(ROUTE_TERMINAL_PAYMENT);
    }
    // If Payment is no longer staged (on Terminal Payment Screen) or Tip is now null(on Tip Input Screen) we navigate home
    if (
      (!isPaymentStaged && pathname === ROUTE_TERMINAL_PAYMENT) ||
      (!tip && pathname === ROUTE_TIPPING_FLOW_INPUT) ||
      (!isCustomerCFD && !showCustomerInfo && pathname === ROUTE_REGISTER_CUSTOMER)
    ) {
      history.push('/');
    }
    // If isCustomerInfoViewVisible is set to true, navigate to /register-customer and show customer info input forms
    if (!isCustomerCFD && showCustomerInfo && pathname === '/') {
      history.push(ROUTE_REGISTER_CUSTOMER);
    }
  }, [history, isPaymentStaged, pathname, showCustomerInfo, tip]);

  return <>{children}</>;
};

const CustomerFacingDisplay: React.FC = () => {
  const dispatch = useDispatch();

  useGetVersion();
  useResizeCFD();
  useClearDisplayBeforeUnload();

  const isHubConnected = useSelector(getIsConnected);

  const {
    logo,
    background,
    isLogoVisible,
    invertLogo,
    isConnectionStatusVisible = false,
  } = useSelector(getCfdOptions);
  const selectedDisplay = useSelector(getSelectedDisplay);
  const isFullscreen = useSelector(getIsFullscreen);

  const { channelName } = useSelector<AppState, ConnectionState>(state => state.connection);

  if (!channelName) {
    return <WaitForRetail />;
  }

  if (!isHubConnected && selectedDisplay && Object.keys(selectedDisplay).length && isFullscreen) {
    dispatch(receiptActions.updateIsSaleVisible(false));
  }

  return (
    <>
      <Page
        backgroundImage={background || DEFAULT_BACKGROUND_IMAGE}
        justifyContent="space-between"
        className="customer-facing-display"
        data-testid="background"
        isSideBarClosed
      >
        <ReceiptBackground>
          {logo && isLogoVisible ? (
            <img
              alt=""
              src={logo}
              width="225px"
              style={{ alignSelf: 'center', ...(invertLogo ? { filter: 'invert(100%)' } : null) }}
              data-testid="logo"
            />
          ) : null}

          <ConfirmSaleButton />
          <div style={{ position: 'absolute', bottom: 0 }}>
            <Logo color="snow" width="100px" />
          </div>

          <StatusIndicator isVisible={isConnectionStatusVisible} />
        </ReceiptBackground>

        <Receipt />
      </Page>
    </>
  );
};

const Wrapper: React.FC = () => (
  <>
    <Helmet>
      {/* meta name="viewport" - viewport settings related to mobile responsiveness  */}
      {/* width=device-width means that it will use the physical width of the device (instead of zooming out) which is good with mobile friendly pages  */}
      {/* initial-scale=1 is the initial zoom, 1 means no zoom  */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {/* Enable standalone (full-screen) mode  */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      {/* Status bar appearance (has no effect unless standalone mode is enabled)  */}
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
    </Helmet>
    <SocketConnection isRelayed={isRenderProcess} isEnabled={isBrowserCFD && !isCustomerCFD}>
      <Router>
        <CFDRouter>
          <Route exact path="/" component={CustomerFacingDisplay} />
          <Route exact path={ROUTE_TIPPING_FLOW_INPUT} component={TipInput} />
          <Route exact path={ROUTE_TERMINAL_PAYMENT} component={TerminalPayment} />
          <Route exact path={ROUTE_REGISTER_CUSTOMER} component={RegisterCustomer} />
        </CFDRouter>
      </Router>
    </SocketConnection>
  </>
);

export default Wrapper;
